import { useEffect, useState } from 'react';
import { preloadAll } from '@/lib/preload';

const FAILSAFE_MS = 45000;

interface PreloaderProps {
  // true once the app is mounted underneath — triggers the fade-out.
  done: boolean;
  // called when all assets are cached (or the failsafe fires).
  onReady: () => void;
}

// Full-screen branded loading gate. Downloads every scene asset (see preload.ts) while the
// page cannot scroll, then hands off to the flight. A wall-clock failsafe guarantees it
// always releases, even if an asset hangs.
export default function Preloader({ done, onReady }: PreloaderProps) {
  const [pct, setPct] = useState(0);

  useEffect(() => {
    const ctrl = new AbortController();
    let settled = false;
    const finish = () => { if (!settled) { settled = true; setPct(100); onReady(); } };
    const failsafe = window.setTimeout(finish, FAILSAFE_MS);

    preloadAll(setPct, ctrl.signal).then(finish, finish);

    return () => { ctrl.abort(); window.clearTimeout(failsafe); };
    // onReady is stable (defined in the parent with a state setter); run this once.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className={'v-preload' + (done ? ' is-done' : '')} aria-hidden={done}>
      <div className="v-preload__inner">
        <div className="v-preload__brand">
          <span className="v-preload__mark" />
          <span className="v-preload__col">
            <span className="v-preload__name">VOUCH</span>
            <span className="v-preload__sub">TECH LOG &amp; CAMO</span>
          </span>
        </div>
        <div
          className="v-preload__track"
          role="progressbar"
          aria-label="Loading the flight"
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={pct}
        >
          <div className="v-preload__bar" style={{ width: pct + '%' }} />
        </div>
        <span className="v-preload__status">Preparing the flight… {pct}%</span>
      </div>
    </div>
  );
}
